import { forwardRef, type ReactNode } from 'react';

import {
  _canReceiveDefaultTextFormat,
  type DataTestId,
  type StylingProps,
} from '@dynatrace/strato-components/core';
import { Flex } from '@dynatrace/strato-components/layouts';
import { Text } from '@dynatrace/strato-components/typography';

//#region Typings
/**
 * Accepted properties for the MessageContainer Title.
 * @public
 */
export interface MessageContainerTitleProps extends StylingProps, DataTestId {
  /** Elements to be displayed in the title slot. */
  children: ReactNode;
}
//#endregion

/**
 * The MessageContainer Title component.
 * @public
 */
export const Title = forwardRef<HTMLDivElement, MessageContainerTitleProps>(
  (props, forwardedRef) => {
    const {
      children,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      ...remainingProps
    }: MessageContainerTitleProps = props;

    const childIsText = _canReceiveDefaultTextFormat(children);

    return (
      <Flex
        data-testid={dataTestId}
        ref={forwardedRef}
        className={consumerClassName}
        style={consumerStyle}
        {...remainingProps}
      >
        {childIsText ? (
          <Text textStyle="base-emphasized">{children}</Text>
        ) : (
          children
        )}
      </Flex>
    );
  },
);

(Title as typeof Title & { displayName: string }).displayName =
  'MessageContainer.Title';
